import React from 'react'
import { config } from '@/lib/config'

interface ErrorBoundaryState {
    hasError: boolean
    error?: Error
    errorInfo?: React.ErrorInfo
}

interface ErrorBoundaryProps {
    children: React.ReactNode
    fallback?: React.ComponentType<{ error: Error; retry: () => void }>
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {
            hasError: true,
            error,
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({
            error,
            errorInfo,
        })

        // Log error in development
        if (config.isDevelopment) {
            console.error('ErrorBoundary caught an error:', error, errorInfo)
        }

        // Report error to external service in production
        if (config.features.errorReporting && config.isProduction) {
            this.reportError(error, errorInfo)
        }
    }

    private reportError = (error: Error, errorInfo: React.ErrorInfo) => {
        // Here you would integrate with your error reporting service
        // For example: Sentry, LogRocket, Bugsnag, etc.
        console.error('Reporting error to external service:', error, errorInfo)
    }

    private handleRetry = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined })
    }

    render() {
        if (this.state.hasError) {
            const { fallback: Fallback } = this.props

            if (Fallback && this.state.error) {
                return <Fallback error={this.state.error} retry={this.handleRetry} />
            }

            return <DefaultErrorFallback error={this.state.error} retry={this.handleRetry} />
        }

        return this.props.children
    }
}

// Default error fallback component
interface DefaultErrorFallbackProps {
    error?: Error
    retry: () => void
}

function DefaultErrorFallback({ error, retry }: DefaultErrorFallbackProps) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="max-w-md w-full text-center">
                <div className="mb-6">
                    <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                        <svg
                            className="w-6 h-6 text-destructive"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 19c-.77.833.192 2.5 1.732 2.5z"
                            />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-foreground mb-2">
                        Oops! Something went wrong
                    </h1>
                    <p className="text-muted-foreground mb-6">
                        We're sorry, but something unexpected happened. Please try again.
                    </p>
                </div>

                {config.isDevelopment && error && (
                    <div className="mb-6 p-4 bg-muted rounded-lg text-left">
                        <h3 className="font-semibold text-sm mb-2">Error Details (Development)</h3>
                        <pre className="text-xs text-muted-foreground overflow-auto">
                            {error.message}
                            {'\n'}
                            {error.stack}
                        </pre>
                    </div>
                )}

                <div className="space-y-3">
                    <button
                        onClick={retry}
                        className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                    >
                        Try Again
                    </button>
                    <button
                        onClick={() => window.location.reload()}
                        className="w-full px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                    >
                        Reload Page
                    </button>
                    <button
                        onClick={() => (window.location.href = '/')}
                        className="w-full px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                        Go to Homepage
                    </button>
                </div>

                <div className="mt-8 text-xs text-muted-foreground">
                    <p>If this problem persists, please contact support.</p>
                    <p>Error ID: {Date.now().toString(36)}</p>
                </div>
            </div>
        </div>
    )
}

// Hook for using error boundary imperatively
export function useErrorHandler() {
    return (error: Error, errorInfo?: React.ErrorInfo) => {
        // This would trigger the error boundary
        throw error
    }
}